import React, { useState, useEffect } from 'react';
import { Bell, Plus, Trash2, Edit2, Power, AlertCircle, Play, ArrowLeft } from 'lucide-react';
import { ultronTuringApi } from '../services/ultronTuringApi';
import { useAppMode } from '../contexts/AppModeContext';
import { useTranslation } from '../contexts/LanguageContext';
import { getDeviceDisplayName, getAllImportedDevices } from '../utils/deviceUtils';
import { getImportedDevices } from '../services/userPreferencesApi';
import { logger } from '../utils/logger';
import NotificationToast from '../components/NotificationToast';
import { getAvailableMetrics, getDefaultMetric, getAvailableConditions, isStateMetric } from '../utils/alertUtils';

interface DeviceAlert {
  id: string;
  name: string;
  device: string;
  deviceName?: string;
  metric: 'temperature' | 'humidity' | 'battery' | 'presence' | 'voltage' | 'current' | 'power' | 'energy' | 'power_state' | 'online' | 'lux' | 'door_state' | 'motion';  // Add state-based metrics, lux, door_state, motion
  condition: 'above' | 'below' | 'equals' | '>=' | '<=' | 'detected' | 'not_detected' | 'on' | 'off' | 'changed' | 'online' | 'offline' | 'opened' | 'closed';  // Add state-based conditions and door conditions
  threshold: number;
  severity: 'info' | 'warning' | 'major' | 'critical';
  notificationChannels: ('websocket' | 'line' | 'toast' | 'system')[];  // Split websocket into toast (in-app) and system (browser notification)
  enabled: boolean;
  triggerMode?: 'edge' | 'continuous' | 'interval';
  intervalMinutes?: number;
  groupId?: string;  // Add groupId to preserve it during updates
}

interface AlertsPageProps {
  onBack: () => void;
}

const AlertsPage: React.FC<AlertsPageProps> = ({ onBack }) => {
  const { mode } = useAppMode(); // Get current board (climate/energy/hvac/lighting)
  const { t } = useTranslation();
  const [alerts, setAlerts] = useState<DeviceAlert[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAlert, setEditingAlert] = useState<DeviceAlert | null>(null);
  const [devices, setDevices] = useState<any[]>([]);
  const [deletingAlertId, setDeletingAlertId] = useState<string | null>(null);

  useEffect(() => {
    // Load devices first, then alerts (to get device names)
    const init = async () => {
      await loadDevices();
      loadAlerts();
    };
    init();
  }, [mode]); // Reload alerts when board changes

  const loadAlerts = async () => {
    try {
      setIsLoading(true);
      logger.log('📥 Loading alerts from server...');

      // Use unified ultronTuringApi for authentication handling
      const result = await ultronTuringApi.queryAlerts() as any;
      logger.log('✅ Loaded alerts from server:', result);

      // MCP response format: { tool: 'QueryAlerts', success: true, alerts: [...], total: N }
      // Or with result wrapper: { result: { success, alerts } }
      const responseData = result?.result || result;
      const alertsArray = responseData.alerts || [];

      // Reverse condition mapping: MCP operator -> UI condition
      const reverseConditionMap: Record<string, string> = {
        'gt': 'above',
        'lt': 'below',
        'eq': 'equals',
        'gte': '>=',
        'lte': '<=',
        'neq': 'not_detected'
      };

      // Check if server returned valid data
      // If not, fall back to localStorage
      if (!responseData.success || responseData.error || responseData.code) {
        logger.warn('⚠️ Server returned error, falling back to localStorage:', responseData);
        const savedAlerts = localStorage.getItem(`${mode}-alerts`);
        if (savedAlerts) {
          try {
            setAlerts(JSON.parse(savedAlerts));
            logger.log(`✅ Loaded ${JSON.parse(savedAlerts).length} alerts from localStorage`);
          } catch (e) {
            logger.error('Failed to parse saved alerts:', e);
          }
        }
        setIsLoading(false);
        return;
      }

      if (alertsArray.length > 0) {
        // Load board devices from Firestore to get display names (intelliSpace only)
        const importedDevicesList = await getImportedDevices('intelliSpace');
        let deviceMap: Record<string, string> = {};

        if (importedDevicesList.length > 0) {
          deviceMap = importedDevicesList.reduce((acc: any, d: any) => {
            const sn = (d as any).deviceSN || d.sn;
            if (sn) {
              acc[sn] = d.displayName || d.nickname || sn;
            }
            return acc;
          }, {});
        }

        // Get imported device IDs for filtering
        const boardDeviceIds: string[] = importedDevicesList
          .map((d: any) => (d as any).deviceSN || d.sn || (d as any).deviceId)
          .filter(Boolean);

        logger.log(`🔍 Filtering alerts for intelliSpace board, imported devices:`, boardDeviceIds);

        // Filter alerts:
        // 1. Exclude Battery Alerts (System Notifications)
        // 2. Only show alerts for current board's imported devices
        // Note: MCP alert structure has conditions.device, conditions.metric, etc.
        const boardAlerts = alertsArray.filter((alert: any) => {
          const alertDevice = alert.conditions?.device || alert.device;
          const alertMetric = alert.conditions?.metric || alert.metric;
          const isBatteryAlert = alertMetric === 'battery';
          const isCurrentBoardDevice = boardDeviceIds.includes(alertDevice);

          // Exclude battery alerts, only include alerts for current board's devices
          return !isBatteryAlert && isCurrentBoardDevice;
        });

        const formattedAlerts = boardAlerts.map((alert: any) => {
          // MCP alert structure: conditions.device, conditions.metric, conditions.operator, conditions.threshold
          const alertDevice = alert.conditions?.device || alert.device;
          const alertMetric = alert.conditions?.metric || alert.metric;
          const alertOperator = alert.conditions?.operator || alert.condition;
          const alertThreshold = alert.conditions?.threshold || alert.threshold;
          const alertSeverity = alert.notifications?.severity || alert.severity;
          // MCP may return ID in different fields: id, ruleId, _id
          const alertId = alert.id || alert.ruleId || alert._id;

          return {
            id: alertId,
            name: alert.name,
            device: alertDevice,
            deviceName: alert.deviceName || deviceMap[alertDevice] || alertDevice,
            metric: alertMetric,
            condition: reverseConditionMap[alertOperator] || alertOperator,
            threshold: alertThreshold,
            severity: alertSeverity || 'warning',
            notificationChannels: alert.notifications?.channels || alert.notificationChannels || ['websocket'],
            enabled: alert.enabled !== false,
            triggerMode: alert.triggerMode || 'edge',
            intervalMinutes: alert.intervalMinutes,
            groupId: alert.groupId  // Preserve groupId from server
          };
        });

        logger.log(`✅ Loaded ${formattedAlerts.length} alerts for ${mode} board (total: ${alertsArray.length}, filtered: ${alertsArray.length - boardAlerts.length})`);
        setAlerts(formattedAlerts);
        // Also save to localStorage for offline access
        localStorage.setItem(`${mode}-alerts`, JSON.stringify(formattedAlerts));
      } else {
        // Server returned empty alerts, try localStorage
        logger.log('📭 Server returned no alerts, checking localStorage...');
        const savedAlerts = localStorage.getItem(`${mode}-alerts`);
        if (savedAlerts) {
          try {
            const parsed = JSON.parse(savedAlerts);
            setAlerts(parsed);
            logger.log(`✅ Loaded ${parsed.length} alerts from localStorage`);
          } catch (e) {
            logger.error('Failed to parse saved alerts:', e);
          }
        }
      }
    } catch (error) {
      logger.error('❌ Failed to load alerts:', error);
      // Fallback to localStorage
      const savedAlerts = localStorage.getItem(`${mode}-alerts`);
      if (savedAlerts) {
        try {
          setAlerts(JSON.parse(savedAlerts));
        } catch (e) {
          logger.error('Failed to parse saved alerts:', e);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadDevices = async () => {
    try {
      // Step 1: Get device SNs from Firestore
      const importedDevices = await getImportedDevices('intelliSpace');
      logger.log(`📱 Raw intelliSpace devices from Firestore:`, importedDevices);
      
      if (importedDevices.length === 0) {
        setDevices([]);
        return;
      }
      
      // Step 2: Extract device SNs
      const deviceSNs = importedDevices.map((d: any) => 
        (d as any).deviceSN || d.sn || (d as any).deviceId
      ).filter(Boolean);
      
      if (deviceSNs.length === 0) {
        logger.warn('⚠️ [AlertsPage] No valid device SNs found in Firestore data');
        setDevices([]);
        return;
      }
      
      // Step 3: Fetch full device information from API
      const baseSNs = Array.from(new Set(deviceSNs.map(sn => sn.split('#')[0])));
      const fullDevices = await ultronTuringApi.queryDevices(undefined, undefined, {
        sns: baseSNs,
        detailed: true
      });
      
      // Merge Firestore data with API data
      const mergedDevices = fullDevices
        .filter(apiDevice => {
          // Filter out devices without a valid SN
          const deviceSN = apiDevice.sn || apiDevice.deviceSN;
          return !!deviceSN;
        })
        .map(apiDevice => {
          const deviceSN = apiDevice.sn || apiDevice.deviceSN;
          if (!deviceSN) {
            // This should not happen due to filter, but TypeScript needs this check
            throw new Error('Device SN is required');
          }
          const baseSN = deviceSN.split('#')[0];
          
          // Find matching Firestore device (match by base SN or full SN)
          const firestoreDevice = importedDevices.find((d: any) => {
            const dSN = (d as any).deviceSN || d.sn || (d as any).deviceId;
            const dBaseSN = dSN ? dSN.split('#')[0] : '';
            return dSN === deviceSN || dBaseSN === baseSN;
          });
          
          return {
            ...apiDevice,
            nickname: firestoreDevice?.nickname || apiDevice.nickname,
            displayName: firestoreDevice?.displayName || apiDevice.displayName,
            sn: deviceSN,
            deviceSN: deviceSN
          };
        });
      
      setDevices(mergedDevices);
    } catch (e) {
      logger.error(`❌ [AlertsPage] Failed to load intelliSpace devices:`, e);
      // Fallback: use Firestore data
      try {
        const devices = await getImportedDevices('intelliSpace');
        setDevices(devices);
        logger.warn(`⚠️ [AlertsPage] Fallback to Firestore-only data: ${devices.length} devices`);
      } catch (fallbackError) {
        logger.error(`❌ [AlertsPage] Fallback also failed:`, fallbackError);
        setDevices([]);
      }
    }
  };

  const saveAlerts = (newAlerts: DeviceAlert[]) => {
    localStorage.setItem(`${mode}-alerts`, JSON.stringify(newAlerts));
    setAlerts(newAlerts);
  };

  const handleCreateAlert = async (alertData: Omit<DeviceAlert, 'id' | 'enabled'>) => {
    try {
      logger.log('🔔 Creating alert:', alertData);

      // Get groupId from credentials or alertData
      let groupId = alertData.groupId;
      if (!groupId) {
        const credentials = localStorage.getItem('ultron-energy-credentials');
        if (credentials) {
          const creds = JSON.parse(credentials);
          groupId = creds.groupId || 'rTtAr4wDmmkQBKTYbLBiNB';
        } else {
          groupId = 'rTtAr4wDmmkQBKTYbLBiNB'; // Default fallback
        }
      }

      // For state-based metrics, threshold should be 0
      const threshold = isStateMetric(alertData.metric) ? 0 : alertData.threshold;
      
      const result = await ultronTuringApi.configureAlert({
        name: alertData.name,
        device: alertData.device,
        metric: alertData.metric,
        condition: alertData.condition,
        threshold: threshold,
        severity: alertData.severity,
        notificationChannels: alertData.notificationChannels,
        triggerMode: alertData.triggerMode,
        intervalMinutes: alertData.intervalMinutes,
        groupId: groupId  // Add groupId to the request
      });

      logger.log('✅ Alert created on server:', result);

      // Extract ID from server response
      // MCP ConfigureAlert returns: { tool: 'ConfigureAlert', result: { success, rule: { id, ... } } }
      const alertId = result?.result?.rule?.id || result?.rule?.id || result?.alert?.id || result?.id;

      if (!alertId) {
        logger.error('❌ Server response missing ID:', result);
        throw new Error(`Server did not return a valid alert ID. Response: ${JSON.stringify(result)}`);
      }

      const newAlert: DeviceAlert = {
        ...alertData,
        id: alertId,
        enabled: true
      };
      saveAlerts([...alerts, newAlert]);
      setShowCreateModal(false);
    } catch (error) {
      logger.error('❌ Failed to create alert:', error);
      logger.warn('Failed to create alert. Please check console for details.');
    }
  };

  const handleUpdateAlert = async (alertData: DeviceAlert) => {
    try {
      logger.log('🔔 Updating alert:', alertData);

      // Validate alert ID before update
      if (!alertData.id || alertData.id.trim() === '') {
        logger.error('❌ Cannot update alert: Missing or empty alert ID');
        logger.warn('無法更新警報：警報 ID 無效。請刪除此警報並重新建立。');
        return;
      }

      // For state-based metrics, threshold should be 0
      const threshold = isStateMetric(alertData.metric) ? 0 : alertData.threshold;
      
      await ultronTuringApi.configureAlert({
        id: alertData.id,
        name: alertData.name,
        device: alertData.device,
        metric: alertData.metric,
        condition: alertData.condition,
        threshold: threshold,
        severity: alertData.severity,
        notificationChannels: alertData.notificationChannels,
        triggerMode: alertData.triggerMode,
        intervalMinutes: alertData.intervalMinutes,
        groupId: alertData.groupId  // Preserve the original groupId
      });

      logger.log('✅ Alert updated on server');

      const updatedAlerts = alerts.map(a => a.id === alertData.id ? alertData : a);
      saveAlerts(updatedAlerts);
      setEditingAlert(null);
    } catch (error) {
      logger.error('❌ Failed to update alert:', error);
      logger.warn('請檢查 console 獲取詳細錯誤訊息');
    }
  };

  const handleDeleteAlert = async (alertId: string) => {
    try {
      logger.log('🗑️ Deleting alert:', alertId);

      await ultronTuringApi.removeConfiguration('alert', alertId);

      logger.log('✅ Alert deleted from server');

      const updatedAlerts = alerts.filter(a => a.id !== alertId);
      saveAlerts(updatedAlerts);
      setDeletingAlertId(null);
    } catch (error) {
      logger.error('❌ Failed to delete alert:', error);
      logger.warn('Failed to delete alert. Please check console for details.');
    }
  };

  const handleToggleAlert = async (alertId: string) => {
    const alert = alerts.find(a => a.id === alertId);
    if (!alert) return;

    const newEnabled = !alert.enabled;
    
    // Update local state immediately for responsive UI
    const updatedAlerts = alerts.map(a =>
      a.id === alertId ? { ...a, enabled: newEnabled } : a
    );
    saveAlerts(updatedAlerts);

    // Sync to server via ConfigureAlert with action: update
    try {
      await ultronTuringApi.configureAlert({
        id: alert.id,
        name: alert.name,
        device: alert.device,
        metric: alert.metric,
        condition: alert.condition,
        threshold: alert.threshold,
        severity: alert.severity,
        notificationChannels: alert.notificationChannels,
        triggerMode: alert.triggerMode,
        intervalMinutes: alert.intervalMinutes,
        enabled: newEnabled
      });
      logger.log(`✅ Alert ${alertId} ${newEnabled ? 'enabled' : 'disabled'} on server`);
    } catch (error) {
      logger.error('❌ Failed to sync alert enabled state to server:', error);
      // Revert local state on error
      const revertedAlerts = alerts.map(a =>
        a.id === alertId ? { ...a, enabled: !newEnabled } : a
      );
      saveAlerts(revertedAlerts);
    }
  };

  const handleTestAlert = async (alertConfig: DeviceAlert) => {
    try {
      logger.log('▶️ Testing alert manually:', alertConfig);

      // Check if Notification API is supported
      if (!('Notification' in window)) {
        logger.error('❌ This browser does not support desktop notifications');
        logger.warn('請使用支援桌面通知的瀏覽器 (Chrome, Firefox, Safari)');
        return;
      }

      logger.log('🔔 Current notification permission:', Notification.permission);

      // Request notification permission if not granted
      if (Notification.permission === 'default') {
        logger.log('📋 Requesting notification permission...');
        const permission = await Notification.requestPermission();
        logger.log('✅ Permission result:', permission);

        if (permission !== 'granted') {
          logger.warn('⚠️ User denied notification permission');
          logger.warn('請在瀏覽器設定中允許通知權限\n\nChrome: 設定 > 隱私權和安全性 > 網站設定 > 通知\nSafari: 偏好設定 > 網站 > 通知');
          return;
        }
      }

      // Show browser notification if permission granted
      if (Notification.permission === 'granted') {
        logger.log('✅ Creating browser notification...');

        const notificationTitle = `🔔 Alert: ${alertConfig.name}`;
        let conditionText = '';
        if (isStateMetric(alertConfig.metric)) {
          // State-based metrics
          if (alertConfig.metric === 'presence') {
            conditionText = alertConfig.condition === 'detected' ? t.alerts.detected : t.alerts.notDetected;
          } else if (alertConfig.metric === 'power_state') {
            conditionText = alertConfig.condition === 'on' ? t.alerts.on : 
                           alertConfig.condition === 'off' ? t.alerts.off : 
                           t.alerts.changed;
          } else if (alertConfig.metric === 'online') {
            conditionText = alertConfig.condition === 'online' ? t.alerts.online : 
                           alertConfig.condition === 'offline' ? t.alerts.offline : 
                           t.alerts.changed;
          }
        } else {
          // Numeric metrics
          conditionText = `${alertConfig.condition} ${alertConfig.threshold}${getMetricUnit(alertConfig.metric)}`;
        }
        const notificationBody = `Device: ${alertConfig.deviceName || alertConfig.device}\nCondition: ${getMetricLabel(alertConfig.metric)} ${conditionText}`;

        logger.log('📧 Notification details:', { title: notificationTitle, body: notificationBody });

        const notification = new Notification(notificationTitle, {
          body: notificationBody,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: alertConfig.id,
          requireInteraction: true,  // Keep notification visible until user interacts
          silent: false,
          vibrate: [200, 100, 200]  // Vibration pattern for mobile
        });

        logger.log('✅ Notification object created:', notification);

        // Listen to notification events
        notification.onclick = () => {
          logger.log('👆 Notification clicked');
          window.focus();
          notification.close();
        };

        notification.onerror = (error) => {
          logger.error('❌ Notification error:', error);
        };

        notification.onshow = () => {
          logger.log('👁️ Notification shown');
        };

        notification.onclose = () => {};

        // Auto close after 5 seconds
        setTimeout(() => {
          logger.log('⏰ Auto-closing notification after 5 seconds');
          notification.close();
        }, 5000);
      } else {
        logger.warn('⚠️ Notification permission denied. Current state:', Notification.permission);
        logger.warn('請在瀏覽器設定中允許通知權限\n\nChrome: 設定 > 隱私權和安全性 > 網站設定 > 通知\nSafari: 偏好設定 > 網站 > 通知');
      }

      // Save to alert history for Alert Center
      const alertHistory = JSON.parse(localStorage.getItem('alert-history') || '[]');
      const newAlert = {
        id: Date.now().toString(),
        alertId: alertConfig.id,
        name: alertConfig.name,
        device: alertConfig.deviceName || alertConfig.device,
        metric: alertConfig.metric,
        condition: alertConfig.condition,
        threshold: alertConfig.threshold,
        severity: alertConfig.severity,
        timestamp: new Date().toISOString(),
        read: false
      };
      alertHistory.unshift(newAlert);
      // Keep only last 50 alerts
      if (alertHistory.length > 50) {
        alertHistory.pop();
      }
      localStorage.setItem('alert-history', JSON.stringify(alertHistory));

      // Get device display name (including nickname if set)
      // Priority: Use current board's devices first, then search all boards
      const currentBoardDevices = devices; // This is already loaded from current mode
      let device = currentBoardDevices.find(d => d.sn === alertConfig.device || (d as any).deviceId === alertConfig.device);

      if (!device) {
        // Fallback: search all imported devices
        const allDevices = getAllImportedDevices();
        device = allDevices.find(d => d.sn === alertConfig.device || d.deviceId === alertConfig.device);
      }

      logger.log('🔍 Found device for toast:', device);

      const deviceName = device ? getDeviceDisplayName(device) : (alertConfig.deviceName || '未知設備');
      logger.log('🔍 Device display name for toast:', deviceName);

      // Prepare toast notification message
      logger.log('🔍 Alert config:', { name: alertConfig.name, device: alertConfig.device, deviceName: alertConfig.deviceName });

      let message = '';
      if (isStateMetric(alertConfig.metric)) {
        // State-based metrics
        if (alertConfig.metric === 'presence') {
          message = alertConfig.condition === 'detected' 
            ? `${deviceName} 偵測到人員`
            : `${deviceName} 未偵測到人員`;
        } else if (alertConfig.metric === 'power_state') {
          message = alertConfig.condition === 'on' 
            ? `${deviceName} 已開啟`
            : alertConfig.condition === 'off'
            ? `${deviceName} 已關閉`
            : `${deviceName} 狀態已變化`;
        } else if (alertConfig.metric === 'online') {
          message = alertConfig.condition === 'online'
            ? `${deviceName} 已上線`
            : alertConfig.condition === 'offline'
            ? `${deviceName} 已離線`
            : `${deviceName} 連線狀態已變化`;
        }
      } else {
        // Numeric metrics
        message = `${deviceName} ${getMetricLabel(alertConfig.metric)} ${alertConfig.condition} ${alertConfig.threshold}`;
      }

      const toastTitle = alertConfig.name || '警報通知';
      logger.log('🔍 Toast notification:', { title: toastTitle, message });

      // Dispatch custom event to update Alert Center badge AND show toast
      window.dispatchEvent(new CustomEvent('new-alert', {
        detail: {
          // For NotificationToast
          id: newAlert.id,
          type: alertConfig.severity,
          title: toastTitle,
          message: message,
          timestamp: newAlert.timestamp,
          // For Alert Center (keep original data)
          alertData: newAlert
        }
      }));

      logger.log('✅ Alert test completed and saved to history');
    } catch (error) {
      logger.error('❌ Failed to test alert:', error);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'info': return 'bg-blue-100 text-blue-800 bg-blue-900/20 text-blue-400';
      case 'warning': return 'bg-yellow-900/20 text-yellow-400';
      case 'major': return 'bg-orange-100 text-orange-800 bg-orange-900/20 text-orange-400';
      case 'critical': return 'bg-red-100 text-red-800 bg-red-900/20 text-red-400';
      default: return 'bg-slate-800/50 text-slate-300';
    }
  };

  const getMetricLabel = (metric: string) => {
    switch (metric) {
      case 'temperature': return t.alerts.temperature;
      case 'humidity': return t.alerts.humidity;
      case 'battery': return t.alerts.battery;
      case 'presence': return t.alerts.presence;
      case 'voltage': return t.alerts.voltage;
      case 'current': return t.alerts.current;
      case 'power': return t.alerts.power;
      case 'energy': return t.alerts.energy;
      case 'power_state': return t.alerts.powerState || '設備狀態';
      case 'online': return t.alerts.onlineStatus || '在線狀態';
      case 'lux': return t.alerts.lux || '照度';
      case 'door_state': return t.alerts.doorState || '門窗狀態';
      case 'motion': return t.alerts.motion || '移動感應';
      default: return metric;
    }
  };

  const getMetricUnit = (metric: string) => {
    switch (metric) {
      case 'temperature': return '°C';
      case 'humidity': return '%';
      case 'battery': return '%';
      case 'presence': return ''; // No unit for presence (boolean)
      case 'voltage': return 'V';
      case 'current': return 'A';
      case 'power': return 'W';
      case 'energy': return 'kWh';
      case 'power_state': return ''; // No unit for state-based metrics
      case 'online': return ''; // No unit for state-based metrics
      case 'lux': return 'lux';
      default: return '';
    }
  };

  // Listen for create alert event from parent
  useEffect(() => {
    const handleOpenCreate = () => setShowCreateModal(true);
    window.addEventListener('openCreateAlert', handleOpenCreate);
    return () => window.removeEventListener('openCreateAlert', handleOpenCreate);
  }, []);

  return (
    <div className="flex-1 flex flex-col">
      <div className="flex-1 overflow-y-auto p-6">
        {isLoading ? (
          <div className="text-center py-20">
            <p className="text-slate-400">{t.alerts.loadingAlerts}</p>
          </div>
        ) : alerts.length === 0 ? (
          <div className="text-center py-20">
            <AlertCircle className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-400 mb-4">{t.alerts.noAlertsConfigured}</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t.alerts.createFirstAlert}
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {alerts.map(alertItem => {
              // Get current device name from devices (real-time lookup)
              // Use priority: nickname > displayName > name > deviceName > sn
              const device = devices.find(d => (d.sn === alertItem.device) || (d.deviceId === alertItem.device));
              const currentDeviceName = device ? getDeviceDisplayName(device) : (alertItem.deviceName || alertItem.device);
              logger.log(`🏷️ Alert device lookup - device: ${alertItem.device}, found:`, device, 'using name:', currentDeviceName);

              return (
              <div
                key={alertItem.id}
                className={`bg-[#0C2340] rounded-lg border-2 p-4 transition-all ${
                  alertItem.enabled
                    ? 'border-blue-200 border-blue-800'
                    : 'border-slate-700/30 opacity-60'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-white">
                        {alertItem.name}
                      </h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSeverityColor(alertItem.severity)}`}>
                        {alertItem.severity.toUpperCase()}
                      </span>
                    </div>

                    <div className="text-sm text-slate-400">
                      <p>
                        {currentDeviceName} | {getMetricLabel(alertItem.metric)} {
                          (() => {
                            if (isStateMetric(alertItem.metric)) {
                              if (alertItem.metric === 'presence' || alertItem.metric === 'motion') {
                                return alertItem.condition === 'detected' ? t.alerts.detected : t.alerts.notDetected;
                              } else if (alertItem.metric === 'door_state') {
                                return alertItem.condition === 'opened' ? (t.alerts.opened || '開啟') : 
                                       alertItem.condition === 'closed' ? (t.alerts.closed || '關閉') : t.alerts.changed;
                              } else if (alertItem.metric === 'power_state') {
                                return alertItem.condition === 'on' ? t.alerts.on : 
                                       alertItem.condition === 'off' ? t.alerts.off : t.alerts.changed;
                              } else if (alertItem.metric === 'online') {
                                return alertItem.condition === 'online' ? t.alerts.online : 
                                       alertItem.condition === 'offline' ? t.alerts.offline : t.alerts.changed;
                              }
                            }
                            return `${alertItem.condition} ${alertItem.threshold}${getMetricUnit(alertItem.metric)}`;
                          })()
                        }
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleAlert(alertItem.id)}
                      className={`p-2 rounded-lg transition-colors ${
                        alertItem.enabled
                          ? 'bg-green-100 text-green-600 hover:bg-green-200 bg-green-900/20 text-green-400'
                          : 'bg-slate-700/50 text-slate-400 hover:bg-slate-800/50 text-slate-400'
                      }`}
                      title={alertItem.enabled ? t.alerts.disable : t.alerts.enable}
                    >
                      <Power className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleTestAlert(alertItem)}
                      className="p-2 text-purple-600 hover:bg-purple-50 text-purple-400 hover:bg-purple-900/20 rounded-lg transition-colors"
                      title={t.alerts.testAlert}
                    >
                      <Play className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setEditingAlert(alertItem)}
                      className="p-2 text-blue-600 text-blue-400 hover:bg-blue-900/20 rounded-lg transition-colors"
                      title={t.alerts.edit}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <div className="relative">
                      <button
                        onClick={() => setDeletingAlertId(deletingAlertId === alertItem.id ? null : alertItem.id)}
                        className="p-2 text-red-600 text-red-400 hover:bg-red-900/20 rounded-lg transition-colors"
                        title={t.alerts.delete}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      {deletingAlertId === alertItem.id && (
                        <div className="absolute right-0 top-12 z-50">
                          <div className="bg-red-600 backdrop-blur-sm text-white px-4 py-2 rounded-lg shadow-xl border border-red-500/50 whitespace-nowrap">
                            <div className="absolute -top-2 right-4 w-4 h-4 bg-red-600 backdrop-blur-sm border-t border-l border-red-500/50 transform rotate-45"></div>
                            <button
                              onClick={() => handleDeleteAlert(alertItem.id)}
                              className="hover:scale-105 transition-transform"
                            >
                              {t.alerts.deleteConfirm}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {(showCreateModal || editingAlert) && (
        <AlertFormModal
          alert={editingAlert}
          devices={devices}
          onSave={editingAlert ? handleUpdateAlert : handleCreateAlert}
          onClose={() => {
            setShowCreateModal(false);
            setEditingAlert(null);
          }}
        />
      )}

      {/* In-page Toast notifications for alert tests */}
      <NotificationToast autoHide={15000} autoRefresh={false} />
    </div>
  );
};

interface AlertFormModalProps {
  alert: DeviceAlert | null;
  devices: any[];
  onSave: (alert: any) => void;
  onClose: () => void;
}

const AlertFormModal: React.FC<AlertFormModalProps> = ({ alert, devices, onSave, onClose }) => {
  const { t } = useTranslation();
  const { mode } = useAppMode(); // Get current board mode
  // Helper to migrate old 'websocket' channel to new 'toast' and 'system' channels
  const migrateChannels = (channels: string[]): ('websocket' | 'line' | 'toast' | 'system')[] => {
    const migrated: ('websocket' | 'line' | 'toast' | 'system')[] = [];
    channels.forEach(ch => {
      if (ch === 'websocket') {
        // Migrate old 'websocket' to both 'toast' and 'system'
        if (!migrated.includes('toast')) migrated.push('toast');
        if (!migrated.includes('system')) migrated.push('system');
      } else if (ch === 'line' || ch === 'toast' || ch === 'system') {
        if (!migrated.includes(ch as any)) migrated.push(ch as any);
      }
    });
    return migrated.length > 0 ? migrated : ['toast'];  // Default to toast
  };

  // Initialize formData with smart defaults based on alert or device
  const getInitialMetric = (): DeviceAlert['metric'] => {
    // If editing, use the existing metric
    if (alert?.metric) {
      return alert.metric;
    }
    
    // If creating and device is already selected, use device's default metric
    if (alert?.device) {
      const device = devices.find(d => (d.sn === alert.device) || (d.deviceId === alert.device));
      if (device) {
        const defaultMetric = getDefaultMetric(device);
        if (defaultMetric) {
          return defaultMetric as DeviceAlert['metric'];
        }
      }
    }
    
    // Fallback: use a generic default (will be updated when device is selected)
    return 'temperature';
  };

  // Get initial condition based on metric
  const getInitialCondition = (metric: string): DeviceAlert['condition'] => {
    if (alert?.condition) {
      return alert.condition;
    }
    const availableConditions = getAvailableConditions(metric);
    return availableConditions[0] as DeviceAlert['condition'];
  };

  const [formData, setFormData] = useState(() => {
    const initialMetric = getInitialMetric();
    return {
      name: alert?.name || '',
      device: alert?.device || '',
      deviceName: alert?.deviceName || '',
      metric: initialMetric,
      condition: getInitialCondition(initialMetric),
      threshold: alert?.threshold || 0,
      severity: alert?.severity || 'warning' as 'info' | 'warning' | 'major' | 'critical',
      notificationChannels: alert?.notificationChannels ? migrateChannels(alert.notificationChannels) : ['toast'] as ('websocket' | 'line' | 'toast' | 'system')[],
      triggerMode: alert?.triggerMode || 'edge' as 'edge' | 'continuous' | 'interval',
      intervalMinutes: alert?.intervalMinutes || 5
    };
  });

  // Sync formData when alert prop changes (e.g., switching from create to edit mode, or editing different alert)
  useEffect(() => {
    if (alert) {
      // If editing, use the existing metric
      const initialMetric = alert.metric || (() => {
        // If creating and device is already selected, use device's default metric
        if (alert.device) {
          const device = devices.find(d => (d.sn === alert.device) || (d.deviceId === alert.device));
          if (device) {
            const defaultMetric = getDefaultMetric(device);
            if (defaultMetric) {
              return defaultMetric as any;
            }
          }
        }
        return 'temperature';
      })();
      
      const availableConditions = getAvailableConditions(initialMetric);
      const initialCondition = alert.condition || availableConditions[0] as any;
      
      setFormData({
        name: alert.name || '',
        device: alert.device || '',
        deviceName: alert.deviceName || '',
        metric: initialMetric,
        condition: initialCondition,
        threshold: alert.threshold || 0,
        severity: alert.severity || 'warning',
        notificationChannels: alert.notificationChannels ? migrateChannels(alert.notificationChannels) : ['toast'],
        triggerMode: alert.triggerMode || 'edge',
        intervalMinutes: alert.intervalMinutes || 5
      });
    } else {
      // Reset to defaults when creating new alert
      setFormData({
        name: '',
        device: '',
        deviceName: '',
        metric: 'temperature',
        condition: 'above',
        threshold: 0,
        severity: 'warning',
        notificationChannels: ['toast'],
        triggerMode: 'edge',
        intervalMinutes: 5
      });
    }
  }, [alert?.id, devices]); // Update when alert ID changes or devices load

  // Update metric and condition when device changes (for edit mode) or when devices load
  useEffect(() => {
    if (formData.device) {
      const device = devices.find(d => (d.sn === formData.device) || (d.deviceId === formData.device));
      if (device) {
        const availableMetrics = getAvailableMetrics(device);
        // If current metric is not available, update to default
        if (!availableMetrics.includes(formData.metric)) {
          const defaultMetric = getDefaultMetric(device);
          if (defaultMetric) {
            const availableConditions = getAvailableConditions(defaultMetric);
            setFormData(prev => ({ 
              ...prev, 
              metric: defaultMetric as any,
              condition: availableConditions[0] as any
            }));
          }
        }
      }
    }
  }, [devices, formData.device]);

  // Update condition when metric changes
  useEffect(() => {
    const availableConditions = getAvailableConditions(formData.metric);
    // If current condition is not available for the new metric, update to first available
    if (!availableConditions.includes(formData.condition)) {
      setFormData(prev => ({ ...prev, condition: availableConditions[0] as any }));
    }
  }, [formData.metric]);

  // Auto-set trigger mode to 'edge' when condition is 'changed' (state change)
  useEffect(() => {
    // Only 'changed' condition is inherently edge-triggered and doesn't need trigger mode selection
    if (formData.condition === 'changed') {
      // Auto-set to edge trigger for state change condition
      if (formData.triggerMode !== 'edge') {
        setFormData(prev => ({ ...prev, triggerMode: 'edge' }));
      }
    }
  }, [formData.condition]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitForm();
  };

  const submitForm = () => {
    if (alert) {
      onSave({ ...alert, ...formData });
    } else {
      onSave(formData);
    }
  };

  const handleDeviceChange = (deviceIdOrSn: string) => {
    const device = devices.find(d => (d.sn === deviceIdOrSn) || (d.deviceId === deviceIdOrSn));
    const availableMetrics = getAvailableMetrics(device);
    
    // If current metric is not available for the new device, switch to default metric
    let newMetric = formData.metric;
    let newCondition = formData.condition;
    
    if (device && !availableMetrics.includes(formData.metric)) {
      const defaultMetric = getDefaultMetric(device);
      if (defaultMetric) {
        newMetric = defaultMetric as any;
        const availableConditions = getAvailableConditions(defaultMetric);
        newCondition = availableConditions[0] as any;
        logger.log(`🔄 Device changed, metric ${formData.metric} not available. Switching to ${newMetric} with condition ${newCondition}`);
      }
    } else if (device && availableMetrics.includes(formData.metric)) {
      // Metric is still available, but check if condition is valid
      const availableConditions = getAvailableConditions(formData.metric);
      if (!availableConditions.includes(formData.condition)) {
        newCondition = availableConditions[0] as any;
        logger.log(`🔄 Condition ${formData.condition} not available for metric ${formData.metric}. Switching to ${newCondition}`);
      }
    }
    
    // Get display name with priority: nickname > displayName > name > deviceName > sn
    const displayName = device?.nickname || 
                       device?.displayName || 
                       device?.name || 
                       device?.deviceName || 
                       deviceIdOrSn;
    
    setFormData({
      ...formData,
      device: deviceIdOrSn,
      deviceName: displayName,
      metric: newMetric,
      condition: newCondition
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-[#0C2340] rounded-lg shadow-xl w-full max-w-2xl h-[90vh] overflow-hidden flex flex-col">
        {/* Fixed Header */}
        <div className="px-6 pt-6 pb-4 border-b border-slate-700/30 flex-shrink-0">
          <h2 className="text-2xl font-bold text-white">
            {alert ? t.alerts.editAlert : t.alerts.createNewAlert}
          </h2>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
          <form id="alert-form" onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                {t.alerts.alertName}
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-700/50 rounded-lg bg-[#0C2340] bg-slate-800/50 text-white"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                {t.alerts.device}
              </label>
              <select
                value={formData.device}
                onChange={e => handleDeviceChange(e.target.value)}
                className="w-full px-3 py-2 border border-slate-700/50 rounded-lg bg-[#0C2340] bg-slate-800/50 text-white"
                required
              >
                <option value="">{t.alerts.selectDevice}</option>
                {devices.map(device => {
                  // Get display name with priority: nickname > displayName > name > deviceName > sn
                  const displayName = device.nickname || 
                                     device.displayName || 
                                     device.name || 
                                     device.deviceName || 
                                     device.sn || 
                                     device.deviceId || 
                                     'Unknown Device';
                  const deviceId = device.sn || device.deviceId;
                  return (
                    <option key={deviceId} value={deviceId}>
                      {displayName} ({deviceId})
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  {t.alerts.metric}
                </label>
                <select
                  value={formData.metric}
                  onChange={e => {
                    const newMetric = e.target.value as any;
                    const availableConditions = getAvailableConditions(newMetric);
                    setFormData({ 
                      ...formData, 
                      metric: newMetric,
                      condition: availableConditions[0] as any
                    });
                  }}
                  className="w-full px-3 py-2 border border-slate-700/50 rounded-lg bg-[#0C2340] bg-slate-800/50 text-white"
                  disabled={!formData.device}
                >
                  {(() => {
                    const selectedDevice = devices.find(d => (d.sn === formData.device) || (d.deviceId === formData.device));
                    const availableMetrics = selectedDevice ? getAvailableMetrics(selectedDevice) : [];
                    
                    if (!formData.device) {
                      return <option value="">{t.alerts.selectDeviceFirst}</option>;
                    }
                    
                    if (availableMetrics.length === 0) {
                      return <option value="">{t.alerts.noMetricsAvailable}</option>;
                    }
                    
                    const metricLabels: Record<string, string> = {
                      temperature: t.alerts.temperature,
                      humidity: t.alerts.humidity,
                      battery: t.alerts.battery,
                      presence: t.alerts.presence,
                      voltage: t.alerts.voltage,
                      current: t.alerts.current,
                      power: t.alerts.power,
                      energy: t.alerts.energy,
                      power_state: t.alerts.powerState || '設備狀態',
                      online: t.alerts.onlineStatus || '在線狀態',
                      lux: t.alerts.lux || '照度',
                      door_state: t.alerts.doorState || '門窗狀態',
                      motion: t.alerts.motion || '移動感應'
                    };
                    
                    return availableMetrics.map(metric => (
                      <option key={metric} value={metric}>
                        {metricLabels[metric] || metric}
                      </option>
                    ));
                  })()}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  {t.alerts.condition}
                </label>
                <select
                  value={formData.condition}
                  onChange={e => setFormData({ ...formData, condition: e.target.value as any })}
                  className="w-full px-3 py-2 border border-slate-700/50 rounded-lg bg-[#0C2340] bg-slate-800/50 text-white"
                  disabled={!formData.device || !formData.metric}
                >
                  {(() => {
                    const availableConditions = getAvailableConditions(formData.metric);
                    
                    if (!formData.device || !formData.metric) {
                      return <option value="">{t.alerts.selectDeviceFirst}</option>;
                    }
                    
                    const conditionLabels: Record<string, string> = {
                      'above': `${t.alerts.above} (>)`,
                      'below': `${t.alerts.below} (<)`,
                      '>=': `${t.alerts.greaterOrEqual} (≥)`,
                      '<=': `${t.alerts.lessOrEqual} (≤)`,
                      'equals': `${t.alerts.equals} (=)`,
                      'detected': t.alerts.detected,
                      'not_detected': t.alerts.notDetected,
                      'on': t.alerts.on || '開啟',
                      'off': t.alerts.off || '關閉',
                      'changed': t.alerts.changed || '狀態變化',
                      'online': t.alerts.online || '在線',
                      'offline': t.alerts.offline || '離線',
                      'opened': t.alerts.opened || '開啟',
                      'closed': t.alerts.closed || '關閉'
                    };
                    
                    return availableConditions.map(condition => (
                      <option key={condition} value={condition}>
                        {conditionLabels[condition] || condition}
                      </option>
                    ));
                  })()}
                </select>
              </div>
            </div>

            {/* Only show threshold input for numeric metrics (not state-based metrics) */}
            {!isStateMetric(formData.metric) && (
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  {t.alerts.thresholdValue}
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={isNaN(formData.threshold) ? '' : formData.threshold}
                  onChange={e => {
                    const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                    setFormData({ ...formData, threshold: isNaN(value) ? 0 : value });
                  }}
                  className="w-full px-3 py-2 border border-slate-700/50 rounded-lg bg-[#0C2340] bg-slate-800/50 text-white"
                  required
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                {t.alerts.severityLevel}
              </label>
              <div className="grid grid-cols-4 gap-2">
                {(['info', 'warning', 'major', 'critical'] as const).map(severity => (
                  <button
                    key={severity}
                    type="button"
                    onClick={() => setFormData({ ...formData, severity })}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      formData.severity === severity
                        ? 'ring-2 ring-blue-500 ring-offset-2'
                        : ''
                    } ${
                      severity === 'info' ? 'bg-blue-100 text-blue-800 bg-blue-900/20 text-blue-400' :
                      severity === 'warning' ? 'bg-yellow-900/20 text-yellow-400' :
                      severity === 'major' ? 'bg-orange-100 text-orange-800 bg-orange-900/20 text-orange-400' :
                      'bg-red-100 text-red-800 bg-red-900/20 text-red-400'
                    }`}
                  >
                    {t.alerts[severity]}
                  </button>
                ))}
              </div>
            </div>

            {/* Trigger Mode - Hide only for 'changed' condition (state change is inherently edge-triggered) */}
            {(() => {
              // Only 'changed' condition doesn't need trigger mode selection (always edge trigger)
              // Other conditions like 'on', 'off', 'online', 'offline' can have different trigger modes
              if (formData.condition === 'changed') {
                return null;
              }
              
              return (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    {t.alerts.triggerMode}
                  </label>
                  <div className="space-y-2">
                <label className="flex items-start gap-3 p-3 border border-slate-700/50 rounded-lg cursor-pointer hover:bg-slate-700/30 hover:bg-slate-700/50/50">
                  <input
                    type="radio"
                    name="triggerMode"
                    value="edge"
                    checked={formData.triggerMode === 'edge'}
                    onChange={(e) => setFormData({ ...formData, triggerMode: e.target.value as 'edge' })}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-white">{t.alerts.edgeTrigger}</div>
                    <div className="text-xs text-slate-400 mt-1">
                      {t.alerts.edgeTriggerDesc}
                    </div>
                  </div>
                </label>

                <label className="flex items-start gap-3 p-3 border border-slate-700/50 rounded-lg cursor-pointer hover:bg-slate-700/30 hover:bg-slate-700/50/50">
                  <input
                    type="radio"
                    name="triggerMode"
                    value="continuous"
                    checked={formData.triggerMode === 'continuous'}
                    onChange={(e) => setFormData({ ...formData, triggerMode: e.target.value as 'continuous' })}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-white">{t.alerts.continuous}</div>
                    <div className="text-xs text-slate-400 mt-1">
                      {t.alerts.continuousDesc}
                    </div>
                  </div>
                </label>

                <label className="flex items-start gap-3 p-3 border border-slate-700/50 rounded-lg cursor-pointer hover:bg-slate-700/30 hover:bg-slate-700/50/50">
                  <input
                    type="radio"
                    name="triggerMode"
                    value="interval"
                    checked={formData.triggerMode === 'interval'}
                    onChange={(e) => setFormData({ ...formData, triggerMode: e.target.value as 'interval' })}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-white">{t.alerts.intervalReminder}</div>
                    <div className="text-xs text-slate-400 mt-1">
                      {t.alerts.intervalReminderDesc}
                    </div>
                    {formData.triggerMode === 'interval' && (
                      <div className="mt-2">
                        <select
                          value={formData.intervalMinutes}
                          onChange={(e) => setFormData({ ...formData, intervalMinutes: Number(e.target.value) })}
                          className="w-full px-3 py-2 border border-slate-700/50 rounded bg-[#0C2340] bg-slate-800/50 text-white text-sm"
                        >
                          <option value={5}>5 {t.alerts.minutes}</option>
                          <option value={10}>10 {t.alerts.minutes}</option>
                          <option value={15}>15 {t.alerts.minutes}</option>
                          <option value={20}>20 {t.alerts.minutes}</option>
                          <option value={25}>25 {t.alerts.minutes}</option>
                          <option value={30}>30 {t.alerts.minutes}</option>
                          <option value={60}>1 {t.alerts.hour}</option>
                          <option value={360}>6 {t.alerts.hours}</option>
                          <option value={720}>12 {t.alerts.hours}</option>
                          <option value={1440}>{t.alerts.onceDaily}</option>
                        </select>
                      </div>
                    )}
                  </div>
                </label>
              </div>
            </div>
              );
            })()}
          </form>
        </div>

        {/* Fixed Footer */}
        <div className="px-6 py-4 border-t border-slate-700/30 flex-shrink-0">
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-300 hover:bg-slate-700/50 rounded-lg transition-colors"
            >
              {t.alerts.cancel}
            </button>
            <button
              type="submit"
              form="alert-form"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {alert ? t.alerts.updateAlert : t.alerts.createAlert}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertsPage;